/**
 * Validates a "where I came from" route param (FLOSEAI-658).
 *
 * A screen reachable from more than one place cannot infer its origin from
 * navigation history — pushing across expo-router groups pops to the other
 * group's root — so the caller states it in the URL. That value then arrives
 * from the address bar, where anyone can put anything, and it is handed
 * straight to `router.replace`. Everything that is not an in-app absolute path
 * is discarded so "go back" can never become "leave the product".
 *
 * Returns the path when it is safe to navigate to, `undefined` otherwise, which
 * callers treat as "no stated origin" and fall back to their own default.
 */
export function parseOriginHref(raw: unknown): string | undefined {
  const first = Array.isArray(raw) ? raw[0] : raw;
  if (typeof first !== 'string') return undefined;

  // The URL parser strips ASCII tab, CR and LF from ANYWHERE in a URL before
  // parsing it, so `/<TAB>/evil.example.com` reaches it as `//evil.example.com`
  // — another host — while the raw string passes a naive `startsWith('//')`
  // check. Normalising first means the checks below see what the parser will,
  // rather than what the attacker wrote.
  const value = first.replace(/[\t\n\r]/g, '').trim();

  // Must be rooted in the app. A relative path resolves against whatever route
  // happens to be current, which is not an origin anyone chose.
  if (!value.startsWith('/')) return undefined;

  // `//host` is protocol-relative and `/\host` is read as one by some parsers:
  // both leave the app despite looking like paths.
  if (value.startsWith('//') || value.startsWith('/\\')) return undefined;

  return value;
}

/**
 * Appends an origin to a route so the destination's back control can return to
 * it. Encoded because the path carries its own slashes and may carry a query.
 */
export function withOriginHref(href: string, origin: string): string {
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}from=${encodeURIComponent(origin)}`;
}
